import type { AxiosResponse } from 'axios';

import { axiosDefault } from 'api/axiosDefault';
import client from 'api/client';
import * as agreement from 'api/rest/agreement';
import * as assessmentAgreement from 'api/rest/assessment_agreement';
import type { AgreementDocumentInterface, AgreementSummary } from 'types';

/**
 * Fetch the retainer summary, then the matching PDF: the signed copy from
 * `summary.pdf_url` once signed, otherwise the unsigned render from `/pdf`.
 */
const fetchAgreementDocument = async (
    getSummary: () => Promise<AxiosResponse<AgreementSummary>>,
    getPdf: () => Promise<AxiosResponse<Blob>>
): Promise<AgreementDocumentInterface> => {
    const { data: summary } = await getSummary();
    const { data: file } =
        summary.signed && summary.pdf_url
            ? await axiosDefault.get<Blob>(summary.pdf_url, { responseType: 'blob' })
            : await getPdf();

    return { file, summary };
};

export const getAgreementDocument = (
    applicationId: number | string
): Promise<AgreementDocumentInterface> =>
    fetchAgreementDocument(
        () => agreement.get(applicationId),
        () => agreement.getPdf(applicationId)
    );

export const getAssessmentAgreementDocument = (
    userId?: number | string
): Promise<AgreementDocumentInterface> =>
    fetchAgreementDocument(
        () => assessmentAgreement.get(userId),
        () => assessmentAgreement.getPdf(userId)
    );

export const postSignAssessmentAgreement = async (payload: FormData) => {
    return client.post('/assessment_agreement', payload, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

export const postSignVisaAgreement = async (applicationId: number, payload: FormData) => {
    return client.post(`/agreement/${applicationId}`, payload, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};
